import type { CSSProperties } from 'react';

/**
 * Style for a block that sits inside one of this site's "author at a fixed reference size, then
 * `transform: scale()` the whole thing to fit the viewport" frames (see `desktop-scale.ts` /
 * `mobile-layout.ts` — Hero and the "How it works" section both work this way) but must not visually
 * grow or shrink along with it: e.g. text that has to read the same size as the same `ds-text-*`
 * class does everywhere else on the site, or a button that has to keep one consistent real height.
 *
 * `scale(1 / frameScale)` on a nested child cancels the ambient scale back out for PAINT only.
 * Layout — line-wrapping, `offsetWidth` measurements, an ancestor auto-sizing to this content — still
 * happens at the block's true, un-scaled size, which is what makes this safe for content whose
 * POSITION must keep tracking the ambient scale (it's still placed by a frame-space `top`/`left` on an
 * ancestor) while its rendered SIZE must not. Do not apply this per-leaf-element inside a group whose
 * members have their own computed relative spacing (e.g. a row of items positioned by measured
 * offsets) — counter-scale the whole group as one block instead, so every member's spacing to its
 * neighbours stays correct; only the group's own position (not its internal layout) should depend on
 * the ambient scale.
 *
 * Caveat this does NOT handle: a `transform` never changes an element's own layout box, so if some
 * ancestor auto-sizes to this content (e.g. `overflow-hidden` with no explicit height), it will still
 * measure the PRE-cancellation size and may clip it. Give that ancestor an explicit real-pixel
 * width/height in that case — see HeroSection.tsx's mobile copy block for a worked example.
 */
export function inverseScaleStyle(frameScale: number, transformOrigin = 'top center'): CSSProperties {
  return { transform: `scale(${1 / frameScale})`, transformOrigin };
}
